import React, { FC } from "react";
import { Box } from "@mui/material";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import RoutingMachine from "./components/RoutingMachine";
import DriversPosition from "./components/DriversPosition";
import MapClickHandler from "pages/MapComponent/components/MapClickHandler";
import { DEFAULT_ZOOM } from "config/constants";
import { useAppSelector } from "store/hooks/useAppSelector";
import { v4 as uuidv4 } from "uuid";
import { shallowEqual } from "react-redux";
import {
  useGetDriverCarPositionQuery,
  useGetOrderDetailsQuery,
} from "store/api";
import { RotatedMarker } from "./components/DriversPosition/DriversPosition";
import { orderStatuses } from "../OrderModal/components/CreatedOrder/constants";
import useMapIcons from "hooks/useMapIcons/useMapIcons";
import "leaflet/dist/leaflet.css";
import styles from "./MapComponent.styled";

const MapComponent: FC = () => {
  const orderId = useAppSelector((state) => state.orderDetails.id);
  const srcAddress = useAppSelector(
    (state) => state.srcAddress.srcAddressRoute,
    shallowEqual,
  );
  const dstAddress = useAppSelector(
    (state) => state.dstAddress.boxes,
    shallowEqual,
  );
  const noRoutes = useAppSelector(
    (state) => state.dstAddress.noRoutes,
    shallowEqual,
  );
  const cityCenter = useAppSelector(
    (state) => state.settings.config.cityCenter,
  );
  const { startPointIcon, generateIcon } = useMapIcons();
  const { data: driverCarPosition, isSuccess } = useGetDriverCarPositionQuery(
    orderId || "",
    {
      skip: !orderId,
      pollingInterval: 5000,
    },
  );
  const { data: orderDetails, isSuccess: isOrderDetailsSuccess } =
    useGetOrderDetailsQuery(orderId || "", {
      skip: !orderId,
      pollingInterval: 5000,
    });

  const validDestinations = dstAddress.filter(
    (dst) => dst.lat !== 0 && dst.lng !== 0,
  );

  return (
    <Box sx={styles.mapContainer}>
      <MapContainer
        center={[cityCenter.lat, cityCenter.lng - 0.07]}
        zoom={DEFAULT_ZOOM}
        style={{ height: "100vh" }}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <MapClickHandler />

        {srcAddress && srcAddress.lat && (
          <Marker
            position={[srcAddress.lat, srcAddress.lng]}
            icon={startPointIcon}
          />
        )}

        {!noRoutes && validDestinations.length && (
          <>
            {validDestinations.map((dst, index) => {
              const icon = generateIcon(dstAddress, index);

              return (
                <Marker
                  key={uuidv4()}
                  position={[dst.lat, dst.lng]}
                  icon={icon}
                />
              );
            })}
          </>
        )}

        {isSuccess &&
          isOrderDetailsSuccess &&
          orderDetails.execution_status === orderStatuses[2].value && (
            <RotatedMarker
              position={[driverCarPosition.lat, driverCarPosition.lng]}
              bearing={driverCarPosition.bearing}
            />
          )}

        {!orderId && <DriversPosition />}

        {!noRoutes && (srcAddress.lat || validDestinations.length) && (
          <RoutingMachine srcAddress={srcAddress} dstAddress={dstAddress} />
        )}
      </MapContainer>
    </Box>
  );
};

export default MapComponent;
